import React, { useState } from 'react'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import { groupSources } from './sourceGroups'

const logoSx = { width: 24, height: 24, fontSize: '0.7rem' }

// Why an unsupported source is marked. Starts with the chip's visible label so the
// accessible name contains it (WCAG 2.5.3, "Label in Name") — and because role='img'
// makes the chip children-presentational, the aria-label replaces that visible text for
// assistive tech rather than supplementing it, so dropping that leading word hides it.
export const EXPERIMENTAL_REASON =
    'Experimental — only paper and book sources reliably return usable candidates.'

// Marks a source ONLY on an explicit `supported: false` from the backend. A payload
// that omits the field (an older backend) yields undefined, which must render no
// markers at all rather than flagging every source — hence the strict comparison
// rather than a falsiness check.
export const isExperimentalSource = (source) => source?.supported === false

// Advisory marker only: the source stays checked, clickable and runnable. The click
// deliberately bubbles to the enclosing MenuItem so the chip is not a dead zone.
//
// role='img' is load-bearing, not decoration: MUI renders a Chip as a plain <div>, and
// an aria-label on a role-less element is ignored by screen readers. The role makes the
// chip a named leaf, so the reason is announced once — and, because a menuitemcheckbox
// takes its name from its contents, it rides along on the row itself rather than only
// appearing on hover.
const ExperimentalChip = () => (
    <Tooltip title={EXPERIMENTAL_REASON}>
        <Chip
            label='Experimental'
            size='small'
            variant='outlined'
            color='warning'
            role='img'
            aria-label={EXPERIMENTAL_REASON}
            sx={{
                marginLeft: '8px',
                height: 20,
            }}
        />
    </Tooltip>
)

// The logo URL comes from the backend registry (CandidateSource.logo). Initials are
// always the children, so both a missing logo (older backend) and a failed image load
// fall back to letters rather than MUI's generic person icon.
const SourceLogo = ({ source }) => (
    <Avatar src={source.logo || undefined} alt='' variant='rounded' sx={logoSx}>
        {source.label.slice(0, 2).toUpperCase()}
    </Avatar>
)

// Per-run candidate source picker: a select-styled button opening a menu of sources
// grouped by resource type, with master and per-group select-all checkboxes. Clicks
// toggle without closing, so multi-toggling is one open/close. Pure selection UI —
// the all-selected → null trigger semantics stay with the caller.
const SourceSelect = ({ sources, selected, onChange, disabled }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const groups = groupSources(sources)
    const allChecked = sources.length > 0 && selected.length === sources.length

    const toggleAll = () => onChange(allChecked ? [] : sources.map(source => source.key))
    const toggleGroup = (group) => {
        const keys = group.sources.map(source => source.key)
        const groupAll = keys.every(key => selected.includes(key))
        onChange(groupAll
            ? selected.filter(key => !keys.includes(key))
            : [...selected, ...keys.filter(key => !selected.includes(key))])
    }
    const toggleOne = (key) => onChange(selected.includes(key)
        ? selected.filter(k => k !== key)
        : [...selected, key])

    return (
        <>
            <Button
                variant='outlined'
                color='inherit'
                disabled={disabled}
                onClick={event => setAnchorEl(event.currentTarget)}
                endIcon={<ArrowDropDownIcon />}
                aria-haspopup='menu'
                aria-expanded={Boolean(anchorEl)}
                sx={{ textTransform: 'none' }}
            >
                Sources: {selected.length} of {sources.length}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem dense role='menuitemcheckbox' aria-checked={allChecked ? true : selected.length > 0 ? 'mixed' : false} onClick={toggleAll}>
                    <Checkbox
                        size='small'
                        checked={allChecked}
                        indeterminate={selected.length > 0 && !allChecked}
                        disableRipple
                        tabIndex={-1}
                        inputProps={{ 'aria-hidden': true }}
                    />
                    <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
                        All sources
                    </ListItemText>
                </MenuItem>
                <Divider />
                {groups.flatMap(group => {
                    const keys = group.sources.map(source => source.key)
                    const checkedCount = keys.filter(key => selected.includes(key)).length
                    return [
                        <MenuItem dense key={group.type} role='menuitemcheckbox' aria-checked={checkedCount === keys.length ? true : checkedCount > 0 ? 'mixed' : false} onClick={() => toggleGroup(group)}>
                            <Checkbox
                                size='small'
                                checked={checkedCount === keys.length}
                                indeterminate={checkedCount > 0 && checkedCount < keys.length}
                                disableRipple
                                tabIndex={-1}
                                inputProps={{ 'aria-hidden': true }}
                            />
                            <Typography variant='caption' sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontWeight: 600 }}>
                                {group.label}
                            </Typography>
                        </MenuItem>,
                        ...group.sources.map(source => (
                            <MenuItem dense key={source.key} role='menuitemcheckbox' aria-checked={selected.includes(source.key)} sx={{ paddingLeft: '28px' }} onClick={() => toggleOne(source.key)}>
                                <Checkbox size='small' checked={selected.includes(source.key)} disableRipple tabIndex={-1} inputProps={{ 'aria-hidden': true }} />
                                <SourceLogo source={source} />
                                <ListItemText sx={{ marginLeft: '10px' }}>{source.label}</ListItemText>
                                {isExperimentalSource(source) && <ExperimentalChip />}
                            </MenuItem>
                        )),
                    ]
                })}
            </Menu>
        </>
    )
}

export default SourceSelect
